import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  makeStyles,
  createTheme,
  ThemeProvider,
} from '@material-ui/core/styles';
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Select,
  MenuItem,
  Chip,
  Radio,
  Checkbox,
  IconButton,
  Box,
  Modal,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloseIcon from '@material-ui/icons/Close';
import numeral from 'numeral';
import moment from 'moment/moment';
import { Backdrop, CircularProgress } from '@mui/material';
import { TradeInCTA } from './components/TradeInCTA';
import { v4 as uuidv4 } from 'uuid';
import { provincesList } from './utils/provinces';
const widgetTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#ffffff' },
    secondary: { main: '#f50057' },
    background: { default: '#333333', paper: '#333333' },
  },
  typography: { fontFamily: ['Source Sans Pro'] },
});

const useStyles = makeStyles((theme) => ({
  tradeinWidget: {
    maxWidth: 550,
    width: '85vw',
    margin: '0 auto',
    padding: theme.spacing(2),
    border: '1px solid #ccc',
    borderRadius: 5,
    marginTop: '25px',
    marginBottom: '25px',
  },
  stepContainer: { marginBottom: theme.spacing(2) },
  button: {
    marginTop: '5px',
    backgroundColor: '#505050',
    color: 'white',
    borderRadius: 5,
    cursor: 'pointer',
    '&:hover': { backgroundColor: '#848383' },
  },
  buttonContainer: { display: 'flex', justifyContent: 'flex-end' },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  chip: {
    padding: theme.spacing(1),
    backgroundColor: '#f2f2f2',
    cursor: 'pointer',
  },
  conditionCommentTitle: { marginTop: '10px', fontWeight: 'bold' },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 400,
    width: '70vw',
    backgroundColor: '#333',
    padding: '20px',
  },
  photosContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  photoInput: { display: 'none' },
  photoUploadButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: 'white',
    borderColor: 'white',
  },
  previewsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  previewItem: {
    position: 'relative',
    width: 70,
    height: 70,
    borderRadius: 4,
    overflow: 'hidden',
    border: '1px solid #555',
    backgroundColor: '#222',
  },
  previewImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  removePhotoBtn: {
    position: 'absolute',
    top: -6,
    right: -6,
    backgroundColor: '#000000aa',
    color: '#fff',
    padding: 2,
    '&:hover': { backgroundColor: '#000000dd' },
  },
}));

function TradeInValuation() {
  const { account } = useParams();
  const baseURL = process.env.REACT_APP_BASE_URL;
  // Hidden steps AFTER reordering (photos now penultimate at step 13): original hidden power/color/equipment -> indices 5,11,12
  const hidden = [5, 11, 12];
  const nextStepButtonLabel = 'Continua';
  const finalStepButtonLabel = 'Conferma';
  const unknownOption = 'Altro';
  const classes = useStyles();
  const [step, setStep] = useState(1);
  const [isPostingForm, setIsPostingForm] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const [photoUploadFailed, setPhotoUploadFailed] = useState(false);
  const [makeOptions, setMakeOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
  const [fuelOptions, setFuelOptions] = useState([]);
  const [powerKWOptions, setPowerKWOptions] = useState([]);
  const [setupOptions, setSetupOptions] = useState([]);
  const [bodyTypeOptions, setBodyTypeOptions] = useState([]);
  const [gearboxOptions, setGearboxOptions] = useState([
    { value: 'A', label: 'Automatico' },
    { value: 'M', label: 'Manuale' },
    { value: 'Altro', label: 'Altro' },
  ]);
  const [conditionOptions] = useState([
    {
      value: 'Usura e danni interni carrozzeria e/o meccanici',
      label: 'Usura e danni interni carrozzeria e/o meccanici',
      description:
        'Visibile usura e danni interni ed esterni con evidenti problematiche meccaniche',
    },
    {
      value: 'Buone condizioni con normali segni di utilizzo',
      label: 'Buone condizioni con normali segni di utilizzo',
      description:
        "L'auto è in buone condizioni con normali segni di utilizzo interni ed esterni (piccoli graffi o ammaccature, ma nessun segno della presenza di fumatori). Buone condizioni meccaniche e guida affidabile.",
    },
    {
      value: 'Ottime condizioni, nessuna usura e/o danno meccanico',
      label: 'Ottime condizioni, nessuna usura e/o danno meccanico',
      description:
        "L'auto è in condizioni perfette nella struttura e nella meccanica, senza danni",
    },
  ]);
  const [colorOptions, setColorOptions] = useState([
    { value: 'Nero', label: 'Nero' },
    { value: 'Bianco', label: 'Bianco' },
    { value: 'Grigio', label: 'Grigio' },
    { value: 'Argento', label: 'Argento' },
    { value: 'Blu', label: 'Blu' },
    { value: 'Rosso', label: 'Rosso' },
    { value: 'Marrone', label: 'Marrone' },
    { value: 'Beige', label: 'Beige' },
    { value: 'Verde', label: 'Verde' },
    { value: 'Arancione', label: 'Arancione' },
    { value: 'Oro', label: 'Oro' },
    { value: 'Giallo', label: 'Giallo' },
    { value: 'Viola', label: 'Viola' },
    { value: 'Turchese', label: 'Turchese' },
    { value: 'Rosa', label: 'Rosa' },
  ]);
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [fuel, setFuel] = useState('');
  const [powerKW, setPowerKW] = useState('');
  const [setup, setSetup] = useState('');
  const [bodyType, setBodyType] = useState('');
  const [gearbox, setGearbox] = useState('');
  const [mileage, setMileage] = useState('');
  const [doors, setDoors] = useState('');
  const [vin, setVin] = useState('');
  const [registrationDate, setRegistrationDate] = useState('');
  const [vehicleCategory, setVehicleCategory] = useState('');
  const [fourWheelDrive, setFourWheelDrive] = useState('');
  const [storageRegion, setStorageRegion] = useState('');
  const [condition, setCondition] = useState('');
  const [additionalCondition, setAdditionalCondition] = useState([]);
  const [color, setColor] = useState('');
  const [equipment, setEquipment] = useState([]);
  const [plate, setPlate] = useState('');
  const [noPlate, setNoPlate] = useState(false);
  const [isCompany, setIsCompany] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [privacyPolicy, setPrivacyPolicy] = useState(false);
  const [photos, setPhotos] = useState([]); // { file, preview }
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState(false);
  const [errorTitle, setErrorTitle] = useState('');
  const [errorDescription, setErrorDescription] = useState('');
  const [eurotaxModalVersionsVisible, setEurotaxModalVersionsVisible] =
    useState(false);
  const [eurotaxData, setEurotaxData] = useState(null);
  const [eurotaxDetail, setEurotaxDetail] = useState(null);
  const [eurotaxVersion, setEurotaxVersion] = useState();
  const useEuroTax = process.env.REACT_APP_EUROTAX_ENABLED === 'true'; // disabled

  //?utm_source=https%3a%2f%2fwww.guidicar.it%2fit-it%2fveicoli%2fmercedes%2fgla%2fmercedes-gla-h247-gla-250-automatic-eq-power-premium-231223005036861%2f
  // &utm_medium=SITO
  // &utm_id=U22045
  const [searchParams] = useSearchParams();
  const siteMedium = searchParams.get('utm_medium');
  const stockId = searchParams.get('utm_id');
  const siteUrl = searchParams.get('utm_source');

  function transformVehicleMake(value) {
    if (value === 'Volkswagen') return 'VW';
    if (value === 'Mercedes-Benz') return 'Mercedes';
    if (value === 'Škoda') return 'Skoda';
    if (value === 'MINI') return 'Mini';
    if (value === 'Citroen') return 'Citroën';
    if (value === 'DR Motor') return 'DR';
    if (value === 'FIAT') return 'Fiat';
    if (value === 'DACIA') return 'Dacia';
    if (value === 'VOLVO') return 'Volvo';

    return value;
  }

  function transformVehicleBodyType(value) {
    if (value === 'Ber') return 'Hatchback';
    if (value === 'SUC') return 'SUV';
    if (value === 'Fam') return 'Stationcar';

    if (value === 'berlina due volumi') return 'Hatchback';
    if (value === 'station wagon') return 'Stationcar';
    if (value === 'Mnv') return 'MPV';

    return undefined;
  }

  const populateFromEurotax = async () => {
    if (eurotaxData) {
      if (
        eurotaxData.hasOwnProperty('marche') &&
        Array.isArray(eurotaxData.marche) &&
        eurotaxData.marche[0] &&
        eurotaxData.marche[0].hasOwnProperty('nome')
      ) {
        setMake(transformVehicleMake(eurotaxData.marche[0].nome));
      }
      if (
        eurotaxData.hasOwnProperty('modelli') &&
        Array.isArray(eurotaxData.modelli) &&
        eurotaxData.modelli[0] &&
        eurotaxData.modelli[0].hasOwnProperty('gruppoStorico') &&
        eurotaxData.modelli[0].gruppoStorico.hasOwnProperty('descrizione')
      ) {
        setModel(eurotaxData.modelli[0].gruppoStorico.descrizione);
      }
      if (eurotaxData.hasOwnProperty('dataImmatricolazione')) {
        setYear(moment(eurotaxData.dataImmatricolazione).format('YYYY'));
        if (
          eurotaxData.hasOwnProperty('dataImmatricolazioneEstero') &&
          eurotaxData.dataImmatricolazioneEstero <
            eurotaxData.dataImmatricolazione
        ) {
          setYear(
            moment(eurotaxData.dataImmatricolazioneEstero).format('YYYY')
          );
        }
      }
      if (
        eurotaxData.hasOwnProperty('porte') &&
        Array.isArray(eurotaxData.porte) &&
        eurotaxData.porte[0]
      ) {
        setDoors(eurotaxData.porte[0]);
      }
      if (eurotaxData.hasOwnProperty('telaio')) {
        setVin(eurotaxData.telaio);
      }
      if (eurotaxData.hasOwnProperty('dataImmatricolazione')) {
        setRegistrationDate(eurotaxData.dataImmatricolazione);
        if (
          eurotaxData.hasOwnProperty('dataImmatricolazioneEstero') &&
          eurotaxData.dataImmatricolazioneEstero <
            eurotaxData.dataImmatricolazione
        ) {
          setRegistrationDate(eurotaxData.dataImmatricolazioneEstero);
        }
      }
      if (eurotaxData.hasOwnProperty('tipoVeicolo')) {
        if (eurotaxData.tipoVeicolo === 'AUTOVETTURE') {
          setVehicleCategory('PV');
        }
        if (eurotaxData.tipoVeicolo === 'VEICOLI_COMM_IND') {
          setVehicleCategory('CV');
        }
      }
    }
    if (eurotaxDetail && eurotaxDetail.modello) {
      var modello = eurotaxDetail.modello;
      if (modello.hasOwnProperty('alimentazione')) {
        setFuel(modello.alimentazione.descrizione);
      }
      if (
        modello.hasOwnProperty('cambio') &&
        modello.cambio.hasOwnProperty('codice')
      ) {
        switch (modello.cambio.codice.charAt(0)) {
          case 'M':
            setGearbox('M');
            break;
          case 'A':
            setGearbox('A');
            break;
          default:
        }
      }
      if (modello.hasOwnProperty('kw')) {
        setPowerKW(modello.kw);
      }
      if (modello.hasOwnProperty('allestimento')) {
        setSetup(modello.allestimento);
      }
      if (
        modello.hasOwnProperty('tipo') &&
        modello.tipo.hasOwnProperty('codice')
      ) {
        setBodyType(transformVehicleBodyType(modello.tipo.codice));
      }
      if (
        modello.hasOwnProperty('trazione') &&
        modello.trazione.hasOwnProperty('codice')
      ) {
        if (modello.trazione.codice === 'I') {
          setFourWheelDrive('Y');
        } else {
          setFourWheelDrive('N');
        }
      }
      if (modello.hasOwnProperty('porte')) {
        setDoors(modello.porte);
      }
    }
    setStep(2);
  };

  const postEuroTax = async () => {
    const body = { plate };

    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    };

    const response = await fetch(
      baseURL + `/widget/eurotax/plate/?${new URLSearchParams({ account })}`,
      requestOptions
    );

    if (!response.ok) {
      console.error('Error fetching Eurotax detail:', response.statusText);
      return null;
    }

    const data = await response.json();
    return data;
  };

  const postEuroTaxDetail = async (motornetCode, motornetVehicleType) => {
    const params = {
      motornetCode: motornetCode,
      motornetVehicleType: motornetVehicleType,
    };

    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    };

    const response = await fetch(
      baseURL + `/widget/eurotax/detail/?${new URLSearchParams({ account })}`,
      requestOptions
    );

    if (!response.ok) {
      console.error('Error fetching Eurotax detail:', JSON.stringify(response));
      return null;
    }

    const data = await response.json();
    return data;
  };

  // Upload photos: aligns with backend (PUT, expects account + filesMeta JSON, optional domain/relationship)
  const uploadPhotos = async (id, params) => {
    if (!photos.length) return true;

    setIsUploadingPhotos(true);
    try {
      const formData = new FormData();
      // Build metadata array (uuid + pattern) keeping order
      const filesMeta = photos.map(() => ({
        uuid: uuidv4(),
        pattern: 'generic',
      }));
      // Append each file with deterministic field name (avoids array normalization ambiguity)
      photos.forEach((p, idx) => {
        const fieldName = `file${idx + 1}.jpg`; // name pattern not used server-side except ordering
        formData.append(fieldName, p.file, p.file.name || fieldName);
      });
      formData.append('filesMeta', JSON.stringify(filesMeta));
      formData.append('account', account); // required by backend
      formData.append('domain', 'widget');
      formData.append('relationship', id); // explicit though defaulted backend-side
      // Pass through a subset of original params if needed for auditing (lowercase mirror)
      if (params?.PLATE) formData.append('plate', params.PLATE);
      if (params?.MAKE) formData.append('make', params.MAKE);
      if (params?.MODEL) formData.append('model', params.MODEL);
      const resp = await fetch(
        baseURL + `/widget/trade-in-valuation/${id}/images`,
        { method: 'PUT', body: formData }
      );
      if (!resp.ok) {
        console.error('Photo upload failed');
        setPhotoUploadFailed(true);
        return false;
      }
      return true;
    } catch (e) {
      console.error('Photo upload exception', e);
      setPhotoUploadFailed(true);
      return false;
    } finally {
      setIsUploadingPhotos(false);
    }
  };

  const postForm = async () => {
    const params = {
      ACCOUNT: account,
      MAKE: make,
      MODEL: model,
      YEAR: year,
      FUEL: fuel,
      POWER_KW: powerKW,
      SETUP: setup,
      BODY_TYPE: bodyType,
      GEARBOX: gearbox,
      MILEAGE: mileage,
      CONDITION: condition,
      ADDITIONAL_CONDITION: additionalCondition,
      COLOR: color,
      EQUIPMENT: equipment,
      PLATE: plate,
      IS_VAT: isCompany ? 'Y' : 'N',
      COMPANYNAME: companyName,
      FIRSTNAME: firstName,
      LASTNAME: lastName,
      EMAIL: email,
      PHONE: phone,
      DOORS: doors,
      VIN: vin,
      REGISTRATION_DATE: registrationDate,
      VEHICLE_CATEGORY: vehicleCategory,
      FOUR_WHEEL_DRIVE_FLAG: fourWheelDrive,
      STORAGE_REGION: storageRegion,
      SITE_URL: siteUrl,
      SITE_MEDIUM: siteMedium ?? 'SITO_HOMEPAGE',
      STOCK_ID: stockId,
    };
    try {
      const response = await fetch(baseURL + '/widget/trade-in-valuation/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      if (!response.ok) {
        setIsPostingForm(false);
        setIsError(true);
        return;
      }
      let id = null;
      try {
        const data = await response.json();
        id = data?.widgetId;
      } catch (e) {
        console.warn('No JSON body or no id in response');
      }
      if (id && photos.length) {
        const photoOk = await uploadPhotos(id, params);
        if (!photoOk) {
          // We still consider main form complete, just flag failure.
          console.warn(
            'Photos failed to upload, proceeding to completion modal'
          );
        }
      }
      setIsPostingForm(false);
      setCompleted(true);
    } catch (e) {
      console.error('Post form failed', e);
      setIsPostingForm(false);
      setIsError(true);
    }
  };

  const checkPlate = async () => {
    if (useEuroTax) {
      const responseVehicle = await postEuroTax();

      if (responseVehicle) {
        setEurotaxData(responseVehicle);
        if (
          responseVehicle &&
          responseVehicle.versioni &&
          responseVehicle.versioni.length > 1
        ) {
          setEurotaxModalVersionsVisible(true);
        } else {
          populateFromEurotax();
        }
      }
    }
  };

  const fetchStepData = async (currentStep, skipAdditional = false) => {
    if (
      (currentStep >= 2 && currentStep < 8) ||
      currentStep === 0 ||
      currentStep === 11 ||
      currentStep === 12 ||
      currentStep === 14
    ) {
      try {
        let params = {};

        const endpoints = [
          'MODEL',
          '',
          'MAKE',
          'YEAR',
          'FUEL',
          'POWER_KW',
          'SETUP',
          'BODY_TYPE',
          'GEARBOX',
          'MILEAGE',
          'CONDITION',
          'COLOR',
          'EQUIPMENT',
          '',
          '', //'SELLER_REGION' - now comes from a static list
        ];

        /*
        if (currentStep > 8 && mileage !== unknownOption) {
          params.MILEAGE = mileage;
        }
        if (currentStep > 7 && gearbox !== unknownOption) {
          params.GEARBOX = gearbox;
        }
        if (currentStep > 6 && bodyType !== unknownOption) {
          params.BODY_TYPE = bodyType;
        }
        if (currentStep > 5 && setup !== unknownOption) {
          params.SETUP = setup;
        }
        if (currentStep > 4 && powerKW !== unknownOption) {
          params.POWER_HP = powerKW;
        }
        if (currentStep > 2 && year !== unknownOption) {
          params.YEAR = year;
        }
        */
        if (currentStep === 7 && !skipAdditional) {
          if (setup !== unknownOption) params.SETUP = setup;
          if (powerKW !== unknownOption && powerKW !== '')
            params.POWER_HP = powerKW;
          if (fuel !== unknownOption) params.FUEL = fuel;
          if (year !== unknownOption) params.YEAR = year;
        }

        if (currentStep === 5 && !skipAdditional) {
          if (year !== unknownOption) params.YEAR = year;
        }

        if (
          currentStep > 4 &&
          currentStep !== 5 &&
          fuel !== unknownOption &&
          !skipAdditional
        ) {
          params.FUEL = fuel;
        }

        if (currentStep > 2 && currentStep < 12) {
          params.MAKE = make;
          params.MODEL = model;
        }

        if (currentStep === 0) {
          params.MAKE = make;
        }

        const requestOptions = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params),
        };

        if (endpoints[currentStep] === 'SELLER_REGION') {
          requestOptions.body = JSON.stringify({});
        }

        const response = await fetch(
          baseURL + '/widget/distinct/' + endpoints[currentStep],
          requestOptions
        );

        const data = await response.json();

        if (
          data.length === 0 &&
          !skipAdditional &&
          (currentStep === 7 || currentStep === 5)
        ) {
          fetchStepData(currentStep, true);
        } else {
          const dataRemapped = [
            ...data.map((item) => {
              return {
                label: item[endpoints[currentStep]],
                value: item[endpoints[currentStep]],
              };
            }),
            currentStep > 1 ? { label: 'Altro', value: 'Altro' } : [],
          ];

          switch (currentStep) {
            case 1:
              break;
            case 0:
              setModelOptions(dataRemapped);
              break;
            case 2:
              setMakeOptions(dataRemapped);
              break;
            case 3:
              setYearOptions(dataRemapped);
              break;
            case 4:
              setFuelOptions(dataRemapped);
              break;
            case 5:
              setPowerKWOptions(dataRemapped);
              break;
            case 6:
              setSetupOptions(dataRemapped);
              break;
            case 7:
              setBodyTypeOptions(dataRemapped);
              break;
            case 8:
              setGearboxOptions(dataRemapped);
              break;
            case 11:
              setColorOptions(dataRemapped);
              break;
            // case 12: equipment options static; no state update needed
            case 14:
              break;
            default:
              break;
          }
        }
      } catch (error) {
        console.error('Errore durante il recupero dei dati:', error);
      }
    }
  };

  useEffect(() => {
    if (make !== '') {
      fetchStepData(0);
    }
    // eslint-disable-next-line
  }, [make]);

  useEffect(() => {
    fetchStepData(step);
    // eslint-disable-next-line
  }, [step]);

  const closeEurotaxModalVersions = () => {
    setEurotaxModalVersionsVisible(false);
  };

  const confirmEurotaxVersionSelection = async () => {
    setEurotaxModalVersionsVisible(false);
    const responseDetail = await postEuroTaxDetail(
      eurotaxVersion,
      eurotaxData.tipoVeicolo
    );
    setEurotaxDetail(responseDetail);
  };

  useEffect(() => {
    if (eurotaxData && eurotaxDetail) {
      console.log(eurotaxData, eurotaxDetail);
      populateFromEurotax();
    }
    // eslint-disable-next-line
  }, [eurotaxData, eurotaxDetail]);

  const resetForm = () => {
    setCompleted(false);
    setIsPostingForm(false);
    setStep(1);
    resetStepsAfter(0);
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  const finalStep = async () => {
    if (lastName === '' || email === '' || phone === '') {
      showError(
        'Attenzione',
        'Per continuare indica il tuo cognome ed i tuoi contatti (e-mail e telefono).'
      );
      return;
    }
    if (!privacyPolicy) {
      showError(
        'Attenzione',
        'Per continuare è necessario accettare la privacy policy.'
      );
      return;
    }
    nextStep();
  };

  const nextStep = async () => {
    let stepIncrement = 1;
    if (validateCurrentStep()) {
      if (step < 14) {
        switch (step) {
          case 4:
            stepIncrement = 2;
            break;
          case 10:
            stepIncrement = 3;
            break;
          default:
            stepIncrement = 1;
            break;
        }
        setStep(step + stepIncrement);
      } else {
        setIsPostingForm(true);

        await postForm();
      }
      //resetStepsAfter(step);
    } else {
      showError('Attenzione', 'Completa i dettagli prima di procedere.');
    }
  };

  const validateCurrentStep = () => {
    switch (step) {
      case 1:
        return plate !== '' || noPlate;
      case 2:
        return make !== '' && model !== '';
      case 3:
        return year !== '';
      case 4:
        return fuel !== '';
      case 5:
        return powerKW !== '';
      case 6:
        return setup !== '';
      case 7:
        return bodyType !== '';
      case 8:
        return gearbox !== '';
      case 9:
        return mileage !== '';
      case 10:
        return condition !== '';
      case 11:
        return color !== '';
      case 12:
        return equipment !== '';
      case 13:
        return true; // photos optional
      case 14:
        return (email !== '' || phone !== '') && privacyPolicy;
      default:
        return true;
    }
  };
  const resetStepsAfter = (currentStep) => {
    if (currentStep < 14) {
      setFirstName('');
      setLastName('');
      setIsCompany(false);
      setCompanyName('');
      setEmail('');
      setPhone('');
    }
    if (currentStep < 13) {
      setPhotos([]);
      setPhotoUploadFailed(false);
    }

    if (currentStep < 12) {
      setEquipment([]);
    }

    if (currentStep < 11) {
      setColor('');
    }
    if (currentStep < 10) {
      setCondition('');
      setAdditionalCondition([]);
    }
    if (currentStep < 9) {
      setMileage('');
    }

    if (currentStep < 8) {
      setGearbox('');
    }

    if (currentStep < 7) {
      setBodyType('');
    }

    if (currentStep < 6) {
      setSetup('');
    }

    if (currentStep < 5) {
      setPowerKW('');
    }

    if (currentStep < 4) {
      setFuel('');
    }

    if (currentStep < 3) {
      setYear('');
    }

    if (currentStep < 2) {
      setMake('');
      setModel('');
    }

    if (currentStep <= 1) {
      setMake('');
      setModel('');
      setYear('');
      setFuel('');
      setPowerKW('');
      setSetup('');
      setBodyType('');
      setGearbox('');
      setMileage('');
      setDoors('');
      setVin('');
      setRegistrationDate('');
      setVehicleCategory('');
      setFourWheelDrive('');
      setCondition('');
      setAdditionalCondition('');
      setColor('');
      setEquipment('');
      setStorageRegion('');
    }

    if (currentStep < 1) {
      setPlate('');
    }
  };

  const handlePhotosSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const existing = photos.length;
    const toAdd = files
      .slice(0, 10 - existing)
      .filter((f) => f.type.startsWith('image/'))
      .map((file) => ({ file, preview: URL.createObjectURL(file) }));
    setPhotos((prev) => [...prev, ...toAdd]);
    e.target.value = '';
  };
  const removePhoto = (idx) => {
    setPhotos((prev) => {
      const clone = [...prev];
      const removed = clone.splice(idx, 1);
      if (removed[0]) URL.revokeObjectURL(removed[0].preview);
      return clone;
    });
  };

  const stepLabels = [
    '',
    'Targa',
    'Marca e modello',
    "Scegli l'anno",
    'Scegli il carburante',
    'Scegli il motore (KW)',
    'Scegli il setup',
    'Scegli la carrozzeria',
    'Scegli il tipo di cambio',
    "Quanti KM ha percorso l'auto?",
    "Quali sono le condizioni dell'auto?",
    'Di che colore è la tua auto?',
    "Seleziona l'equipaggiamento",
    'Foto (opzionale)',
    'Informazioni di contatto',
  ];

  const getFieldValueByStep = (step) => {
    switch (step) {
      case 1:
        return 'Targa: ' + plate;
      case 2:
        let makeLabel = makeOptions.filter((option) => option.value === make);
        let modelLabel = modelOptions.filter(
          (option) => option.value === model
        );
        if (makeLabel.length > 0 && modelLabel.length > 0) {
          return makeLabel[0].label + ' ' + modelLabel[0].label;
        } else {
          return '';
        }
      case 3:
        return year;
      case 4:
        return fuel;
      case 5:
        return powerKW + ' KW';
      case 6:
        return setup;
      case 7:
        return bodyType;
      case 8:
        let gearboxLabel = gearboxOptions.filter(
          (option) => option.value === gearbox
        );
        if (gearboxLabel.length > 0) {
          return gearboxLabel[0].label;
        } else {
          return '';
        }
      case 9:
        return mileage;
      case 10:
        let conditionLabel = conditionOptions.filter(
          (option) => option.value === condition
        );
        if (conditionLabel.length > 0) {
          return 'Condizione: ' + conditionLabel[0].label;
        } else {
          return '';
        }
      case 11:
        return color;
      case 12:
        return (equipment || []).join(', ');
      case 13:
        return photos.length ? `${photos.length} foto aggiunte` : '';
      case 14:
        return email;
      default:
        return '';
    }
  };

  const handleAdditionalConditionSelect = (selectedOption) => {
    if (additionalCondition.includes(selectedOption)) {
      setAdditionalCondition(
        additionalCondition.filter((option) => option !== selectedOption)
      );
    } else {
      setAdditionalCondition([...additionalCondition, selectedOption]);
    }
  };

  const additionalConditionOptions = [
    'Non fumatore',
    'Cronologia tagliandi completa',
    'Incidentata',
    'Revisionata',
    'Tagliandata',
  ];

  const handleEquipmentSelect = (selectedOption) => {
    if (equipment.includes(selectedOption)) {
      setEquipment(equipment.filter((option) => option !== selectedOption));
    } else {
      setEquipment([...equipment, selectedOption]);
    }
  };

  const equipmentOptions = [
    'Android auto',
    'Apple CarPlay',
    'Bagagliaio elettronico',
    'Bluetooth',
    'Cerchi in lega',
    'Climatizzatore',
    'Climatizzatore automatico',
    'Cruise control',
    'Fari LED',
    'Fari Xenon',
    'Fendinebbia',
    'Filtro antiparticolato',
    'Gancio di traino',
    'Headup display',
    'Interni in pelle',
    'Night View Assistant',
    'Pacchetto sport',
    'Pelle parziale',
    'Riconoscimento dei segnali stradali',
    'Sedili elettrici',
    'Sedili riscaldati',
    'Sedili sportivi',
    'Sensore pioggia',
    'Sensori angolo cieco',
    'Sensori parcheggio',
    'Sistema di navigazione',
    'Stop&Go',
    'Telecamera per parcheggio',
    'Tetto apribile',
    'Tetto in vetro',
    'Volante in pelle',
    'Volante multifunzione',
    'Volante riscaldato',
  ];

  const toggleAccordion = (selectedStep) => {
    if (selectedStep < step) {
      setStep(selectedStep);
    }
  };

  const AccordionStep = ({ step }) => {
    return (
      <AccordionSummary
        iconbuttonprops={{ onClick: toggleAccordion }}
        onClick={() => toggleAccordion(step)}
        expandIcon={
          <>
            <IconButton>
              <ExpandMoreIcon />
            </IconButton>
          </>
        }
      >
        <Typography>
          {getFieldValueByStep(step) !== null &&
          ((!Array.isArray(getFieldValueByStep(step)) &&
            getFieldValueByStep(step).trim() !== '') ||
            (Array.isArray(getFieldValueByStep(step)) &&
              getFieldValueByStep(step).length === 0))
            ? getFieldValueByStep(step)
            : stepLabels[step]}
        </Typography>
      </AccordionSummary>
    );
  };

  const showError = (title, description) => {
    setErrorTitle(title);
    setErrorDescription(description);
    setError(true);
  };

  return (
    <ThemeProvider theme={widgetTheme}>
      <div
        className={classes.tradeinWidget}
        style={{
          background:
            'linear-gradient(135deg, #232526 0%, #414345 50%, #232526 100%)',
          boxShadow: '0 4px 32px 0 rgba(0,0,0,0.25)',
        }}
      >
        <TradeInCTA />
        {/* Error Modal */}
        <Modal
          open={error}
          onClose={() => setError(false)}
          aria-labelledby='alert-modal-title'
        >
          <Box className={classes.modal}>
            <Typography variant='h6'>{errorTitle}</Typography>
            <Typography style={{ marginTop: 8 }}>{errorDescription}</Typography>
            <div className={classes.buttonContainer}>
              <Button
                className={classes.button}
                onClick={() => setError(false)}
              >
                Chiudi
              </Button>
            </div>
          </Box>
        </Modal>
        {/* Completed Modal */}
        <Modal
          open={completed}
          onClose={resetForm}
          aria-labelledby='completed-modal-title'
        >
          <Box className={classes.modal}>
            <Typography variant='h6'>
              Abbiamo ricevuto la tua richiesta.
            </Typography>
            <Typography style={{ marginTop: 8 }}>
              Riceverai una quotazione della tua auto a breve.
            </Typography>
            <div className={classes.buttonContainer}>
              <Button className={classes.button} onClick={resetForm}>
                Chiudi
              </Button>
            </div>
          </Box>
        </Modal>
        {/* Post error */}
        <Modal open={isError} aria-labelledby='post-error-title'>
          <Box className={classes.modal}>
            <Typography variant='h6'>
              Si è verificato un errore durante l'invio della richiesta.
            </Typography>
            <Typography id='modal-modal-description' sx={{ mt: 2 }}>
              Ti preghiamo di riprovare.
            </Typography>
            <div className={classes.buttonContainer}>
              <Button
                className={classes.button}
                onClick={() => setIsError(false)}
              >
                Chiudi
              </Button>
            </div>
          </Box>
        </Modal>
        {/* Eurotax versions */}
        <Modal
          open={eurotaxModalVersionsVisible}
          onClose={() => setEurotaxModalVersionsVisible(false)}
        >
          <Box className={classes.modal}>
            <Typography variant='h6'>Seleziona la versione</Typography>
            <FormControl fullWidth style={{ marginTop: 12 }}>
              <InputLabel>Versione</InputLabel>
              <Select
                value={eurotaxVersion}
                onChange={(e) => setEurotaxVersion(e.target.value)}
              >
                {eurotaxData?.versioni?.map((v) => (
                  <MenuItem
                    key={v.codiceMotornetUnivoco}
                    value={v.codiceMotornetUnivoco}
                  >
                    {v.versione}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <div className={classes.buttonContainer}>
              <Button
                className={classes.button}
                onClick={confirmEurotaxVersionSelection}
              >
                Continua
              </Button>
            </div>
          </Box>
        </Modal>
        <h2>Ricevi una valutazione della tua auto senza impegno</h2>
        {/* Step 1 - Plate */}
        <Accordion expanded={step === 1} className={classes.stepContainer}>
          <AccordionStep step={1} />
          <AccordionDetails style={{ flexDirection: 'column' }}>
            <TextField
              label='La targa del veicolo'
              value={plate}
              onChange={(e) => setPlate(e.target.value)}
              fullWidth
              required
            />
            {false && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={noPlate}
                    onChange={(e) => setNoPlate(e.target.checked)}
                  />
                }
                label='Salta questo passaggio'
              />
            )}

            <div className={classes.buttonContainer}>
              <Button
                className={classes.button}
                onClick={useEuroTax ? checkPlate : () => setStep(2)}
              >
                {nextStepButtonLabel}
              </Button>
            </div>
          </AccordionDetails>
        </Accordion>
        {/* Step 2 - Make/Model */}
        <Accordion expanded={step === 2} className={classes.stepContainer}>
          <AccordionStep step={2} />
          <AccordionDetails style={{ flexDirection: 'column' }}>
            <FormControl fullWidth style={{ marginBottom: 12 }}>
              <InputLabel>Marca</InputLabel>
              <Select value={make} onChange={(e) => setMake(e.target.value)}>
                {makeOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Modello</InputLabel>
              <Select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                disabled={!make}
              >
                {modelOptions?.map((modelOption) => (
                  <MenuItem key={modelOption.value} value={modelOption.value}>
                    {modelOption.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <div className={classes.buttonContainer}>
              <Button className={classes.button} onClick={nextStep}>
                {nextStepButtonLabel}
              </Button>
            </div>
          </AccordionDetails>
        </Accordion>
        {/* Step 3 - Year */}
        <Accordion expanded={step === 3} className={classes.stepContainer}>
          <AccordionStep step={3} />
          <AccordionDetails style={{ flexDirection: 'column' }}>
            <div className={classes.chipContainer}>
              {yearOptions.map((option) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  clickable
                  onClick={() => setYear(option.value)}
                  color={year === option.value ? 'primary' : 'default'}
                  className={classes.chip}
                />
              ))}
            </div>
            <div className={classes.buttonContainer}>
              <Button className={classes.button} onClick={nextStep}>
                {nextStepButtonLabel}
              </Button>
            </div>
          </AccordionDetails>
        </Accordion>
        {/* Step 4 - Fuel */}
        <Accordion expanded={step === 4} className={classes.stepContainer}>
          <AccordionStep step={4} />
          <AccordionDetails style={{ flexDirection: 'column' }}>
            <div className={classes.chipContainer}>
              {fuelOptions.map((option) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  clickable
                  onClick={() => setFuel(option.value)}
                  color={fuel === option.value ? 'primary' : 'default'}
                  className={classes.chip}
                />
              ))}
            </div>
            <div className={classes.buttonContainer}>
              <Button className={classes.button} onClick={nextStep}>
                {nextStepButtonLabel}
              </Button>
            </div>
          </AccordionDetails>
        </Accordion>
        {/* Step 5 - PowerKW (maybe hidden) */}
        {!hidden.includes(5) && (
          <Accordion expanded={step === 5} className={classes.stepContainer}>
            <AccordionStep step={5} />
            <AccordionDetails style={{ flexDirection: 'column' }}>
              <div className={classes.chipContainer}>
                {powerKWOptions.map((option) => (
                  <Chip
                    key={option.value}
                    label={numeral(parseInt(option.label)).format('#,##0')}
                    clickable
                    onClick={() =>
                      setPowerKW(
                        numeral(parseInt(option.value)).format('#,##0')
                      )
                    }
                    color={
                      powerKW ===
                      numeral(parseInt(option.value)).format('#,##0')
                        ? 'primary'
                        : 'default'
                    }
                    className={classes.chip}
                  />
                ))}
              </div>
              <div className={classes.buttonContainer}>
                <Button className={classes.button} onClick={nextStep}>
                  {nextStepButtonLabel}
                </Button>
              </div>
            </AccordionDetails>
          </Accordion>
        )}
        {/* Step 6 - Setup */}
        <Accordion expanded={step === 6} className={classes.stepContainer}>
          <AccordionStep step={6} />
          <AccordionDetails style={{ flexDirection: 'column' }}>
            <div className={classes.chipContainer}>
              {setupOptions.map((option) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  clickable
                  onClick={() => setSetup(option.value)}
                  color={setup === option.value ? 'primary' : 'default'}
                  className={classes.chip}
                />
              ))}
            </div>
            <div className={classes.buttonContainer}>
              <Button className={classes.button} onClick={nextStep}>
                {nextStepButtonLabel}
              </Button>
            </div>
          </AccordionDetails>
        </Accordion>
        {/* Step 7 - Body Type */}
        <Accordion expanded={step === 7} className={classes.stepContainer}>
          <AccordionStep step={7} />
          <AccordionDetails style={{ flexDirection: 'column' }}>
            <div className={classes.chipContainer}>
              {bodyTypeOptions.map((option) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  clickable
                  onClick={() => setBodyType(option.value)}
                  color={bodyType === option.value ? 'primary' : 'default'}
                  className={classes.chip}
                />
              ))}
            </div>
            <div className={classes.buttonContainer}>
              <Button className={classes.button} onClick={nextStep}>
                {nextStepButtonLabel}
              </Button>
            </div>
          </AccordionDetails>
        </Accordion>
        {/* Step 8 - Gearbox */}
        <Accordion expanded={step === 8} className={classes.stepContainer}>
          <AccordionStep step={8} />
          <AccordionDetails style={{ flexDirection: 'column' }}>
            <div className={classes.chipContainer}>
              {gearboxOptions.map((option) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  clickable
                  onClick={() => setGearbox(option.value)}
                  color={gearbox === option.value ? 'primary' : 'default'}
                  className={classes.chip}
                />
              ))}
            </div>
            <div className={classes.buttonContainer}>
              <Button className={classes.button} onClick={nextStep}>
                {nextStepButtonLabel}
              </Button>
            </div>
          </AccordionDetails>
        </Accordion>
        <Accordion expanded={step === 9} className={classes.stepContainer}>
          <AccordionStep step={9} />
          <AccordionDetails style={{ flexDirection: 'column' }}>
            <TextField
              label="Quanti KM ha percorso l'auto?"
              value={mileage}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '');
                setMileage(val);
              }}
              fullWidth
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            />
            <div className={classes.buttonContainer}>
              <Button className={classes.button} onClick={nextStep}>
                {nextStepButtonLabel}
              </Button>
            </div>
          </AccordionDetails>
        </Accordion>
        {/* Step 10 - Condition */}
        <Accordion expanded={step === 10} className={classes.stepContainer}>
          <AccordionStep step={10} />
          <AccordionDetails style={{ flexDirection: 'column' }}>
            <div>
              <Typography variant='subtitle1'>
                Seleziona una condizione:
              </Typography>
              {conditionOptions.map((option) => (
                <div>
                  <Radio
                    checked={condition === option.value}
                    onChange={() => setCondition(option.value)}
                    value={option.value}
                    name='condition-radio'
                  />
                  <label>{option.label}</label>
                  <Typography variant='body2'>{option.description}</Typography>
                </div>
              ))}
            </div>
            <Typography
              className={classes.conditionCommentTitle}
              variant='subtitle2'
            >
              Commenti (opzionale):
            </Typography>
            <div className={classes.chipContainer}>
              {additionalConditionOptions.map((option) => (
                <Chip
                  key={option}
                  label={option}
                  clickable
                  onClick={() => handleAdditionalConditionSelect(option)}
                  color={
                    additionalCondition.includes(option) ? 'primary' : 'default'
                  }
                  className={classes.chip}
                />
              ))}
            </div>
            <div className={classes.buttonContainer}>
              <Button className={classes.button} onClick={nextStep}>
                {nextStepButtonLabel}
              </Button>
            </div>
          </AccordionDetails>
        </Accordion>
        {/* Step 11 - Color */}
        {!hidden.includes(11) && (
          <Accordion expanded={step === 11} className={classes.stepContainer}>
            <AccordionStep step={11} />
            <AccordionDetails style={{ flexDirection: 'column' }}>
              <div className={classes.chipContainer}>
                {colorOptions.map((option) => (
                  <Chip
                    key={option.value}
                    label={option.label}
                    clickable
                    onClick={() => setColor(option.value)}
                    color={color === option.value ? 'primary' : 'default'}
                    className={classes.chip}
                  />
                ))}
              </div>
              <div className={classes.buttonContainer}>
                <Button className={classes.button} onClick={nextStep}>
                  {nextStepButtonLabel}
                </Button>
              </div>
            </AccordionDetails>
          </Accordion>
        )}
        {/* Step 12 - Equipment */}
        {!hidden.includes(12) && (
          <Accordion expanded={step === 12} className={classes.stepContainer}>
            <AccordionStep step={12} />
            <AccordionDetails style={{ flexDirection: 'column' }}>
              <div className={classes.chipContainer}>
                {equipmentOptions.map((option) => (
                  <Chip
                    key={option}
                    label={option}
                    clickable
                    onClick={() => handleEquipmentSelect(option)}
                    color={equipment.includes(option) ? 'primary' : 'default'}
                    className={classes.chip}
                  />
                ))}
              </div>
              <div className={classes.buttonContainer}>
                <Button className={classes.button} onClick={nextStep}>
                  {nextStepButtonLabel}
                </Button>
              </div>
            </AccordionDetails>
          </Accordion>
        )}
        {/* Step 13 - Photos */}
        <Accordion expanded={step === 13} className={classes.stepContainer}>
          <AccordionStep step={13} />
          <AccordionDetails style={{ flexDirection: 'column' }}>
            <div className={classes.photosContainer}>
              <Typography variant='subtitle1'>
                Carica fino a 10 foto (opzionale)
              </Typography>
              <input
                id='photos-upload-input'
                type='file'
                accept='image/*'
                multiple
                className={classes.photoInput}
                onChange={handlePhotosSelected}
              />
              <label htmlFor='photos-upload-input'>
                <Button
                  variant='outlined'
                  component='span'
                  className={classes.photoUploadButton}
                  disabled={photos.length >= 10}
                >
                  {photos.length >= 10
                    ? 'Limite massimo raggiunto'
                    : 'Carica foto'}
                </Button>
              </label>
              {photos.length > 0 && (
                <div className={classes.previewsGrid}>
                  {photos.map((p, idx) => (
                    <div key={idx} className={classes.previewItem}>
                      <img
                        src={p.preview}
                        alt={`foto-${idx + 1}`}
                        className={classes.previewImg}
                      />
                      <IconButton
                        size='small'
                        className={classes.removePhotoBtn}
                        onClick={() => removePhoto(idx)}
                        aria-label='Rimuovi'
                      >
                        <CloseIcon style={{ fontSize: 14 }} />
                      </IconButton>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className={classes.buttonContainer}>
              <Button className={classes.button} onClick={nextStep}>
                {nextStepButtonLabel}
              </Button>
            </div>
          </AccordionDetails>
        </Accordion>
        {/* Step 14 - Contact */}
        <Accordion expanded={step === 14} className={classes.stepContainer}>
          <AccordionStep step={14} />
          <AccordionDetails style={{ flexDirection: 'column' }}>
            <TextField
              label='Nome'
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
            />
            <TextField
              label='Cognome'
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              required
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isCompany}
                  onChange={(e) => setIsCompany(e.target.checked)}
                />
              }
              label="Sei un'azienda?"
            />
            {isCompany && (
              <TextField
                label='Nome Azienda'
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                fullWidth
                visible={isCompany}
              />
            )}
            <TextField
              label='Il tuo indirizzo email'
              type='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label='Il tuo numero di telefono'
              type='phone'
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              fullWidth
              required
            />
            <FormControl fullWidth style={{ marginTop: 12 }}>
              <InputLabel>Provincia</InputLabel>
              <Select
                value={storageRegion}
                onChange={(e) => setStorageRegion(e.target.value)}
              >
                {provincesList.map((prov) => (
                  <MenuItem key={prov.code} value={prov.code}>
                    {prov.name} - {prov.code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={privacyPolicy}
                  onChange={(e) => setPrivacyPolicy(e.target.checked)}
                />
              }
              label='Accettazione privacy policy'
            />
            <div className={classes.buttonContainer}>
              <Button
                className={classes.button}
                onClick={finalStep}
                disabled={isPostingForm}
              >
                {finalStepButtonLabel}
              </Button>
            </div>
          </AccordionDetails>
        </Accordion>
        <Backdrop open={isPostingForm || isUploadingPhotos}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <CircularProgress sx={{ color: '#fff' }} />
            <Typography variant='body2'>
              {isPostingForm
                ? 'Invio dati...'
                : isUploadingPhotos
                ? 'Caricamento foto...'
                : ''}
            </Typography>
            {photoUploadFailed && (
              <Typography variant='caption' style={{ color: '#ff8080' }}>
                Alcune foto non sono state caricate.
              </Typography>
            )}
          </div>
        </Backdrop>
      </div>
    </ThemeProvider>
  );
}

export default TradeInValuation;
